import { Loader2, Mail, Phone } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatDateTime } from "@/lib/format";
import { OwnerPicker } from "./OwnerPicker";
import { FINANCE_STATUS_OPTIONS, STAGE_STYLES } from "./dashboardTokens";

/** Name, company and the two ways to reach them, both one tap away. */
const ContactBlock = ({ enquiry }) => (
  <div className="rounded-lg border border-border bg-[color:var(--tt-surface-2)] p-4 text-sm">
    <p className="tt-eyebrow">Contact details</p>
    <p className="mt-2 font-semibold" data-testid="admin-finance-detail-name">
      {enquiry.full_name}
    </p>
    <p className="text-muted-foreground">{enquiry.company}</p>
    <p className="mt-3 flex flex-wrap gap-2">
      {enquiry.phone ? (
        <Button
          asChild
          variant="outline"
          size="sm"
          className="h-9 bg-white"
          data-testid="admin-finance-detail-call"
        >
          <a href={`tel:${enquiry.phone}`}>
            <Phone className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
            {enquiry.phone_display || enquiry.phone}
          </a>
        </Button>
      ) : null}
      {enquiry.email ? (
        <Button
          asChild
          variant="outline"
          size="sm"
          className="h-9 bg-white"
          data-testid="admin-finance-detail-email"
        >
          <a href={`mailto:${enquiry.email}?subject=Your%20outsourced%20finance%20enquiry%20${enquiry.reference}`}>
            <Mail className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
            {enquiry.email}
          </a>
        </Button>
      ) : null}
    </p>
  </div>
);

/** Every answer they gave, in the order the form asked for it. */
const BriefBlock = ({ summary }) => (
  <div className="rounded-lg border border-border p-4" data-testid="admin-finance-detail-brief">
    <p className="tt-eyebrow">The brief</p>
    <dl className="mt-3 space-y-3">
      {summary.map((line) => (
        <div key={line.label} className="grid gap-0.5 sm:grid-cols-[180px_1fr] sm:gap-4">
          <dt className="text-[10px] font-bold uppercase tracking-[0.12em] text-primary">
            {line.label}
          </dt>
          <dd className="text-sm leading-relaxed">{line.value}</dd>
        </div>
      ))}
    </dl>
  </div>
);

/**
 * One outsourced-finance scoping enquiry, in full, with the only action the
 * team needs on it: where it has got to.
 *
 * There is no price to show and nothing to recalculate — this service is
 * scoped by a person — so the panel is the brief plus a status.
 */
export const FinanceEnquiryDialog = ({
  enquiry,
  saving,
  onStatusChange,
  onClose,
  team = [],
  savingOwner,
  onAssignOwner,
  onAddTeamMember,
}) => (
  <Dialog open={Boolean(enquiry)} onOpenChange={(open) => (open ? null : onClose())}>
    <DialogContent
      data-testid="admin-finance-detail-dialog"
      className="max-h-[90vh] max-w-2xl overflow-y-auto"
    >
      {enquiry ? (
        <>
          <DialogHeader>
            <DialogTitle className="flex flex-wrap items-center gap-2">
              Outsourced finance enquiry
              <Badge
                variant="outline"
                data-testid="admin-finance-detail-status-badge"
                className={`font-normal ${STAGE_STYLES[enquiry.status] || STAGE_STYLES.new}`}
              >
                {enquiry.status_label}
              </Badge>
            </DialogTitle>
            <DialogDescription>
              <span className="font-amount">{enquiry.reference}</span>
              {" · "}
              Received {formatDateTime(enquiry.created_at)}
              {enquiry.acknowledged ? " · confirmation emailed" : ""}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <ContactBlock enquiry={enquiry} />
            <BriefBlock summary={enquiry.summary || []} />

            {enquiry.message ? (
              <div className="rounded-lg border border-border p-4">
                <p className="tt-eyebrow">In their own words</p>
                <p
                  className="mt-2 whitespace-pre-line text-sm leading-relaxed"
                  data-testid="admin-finance-detail-message"
                >
                  {enquiry.message}
                </p>
              </div>
            ) : null}

            <div className="rounded-lg border border-border p-4">
              <Label
                htmlFor="finance-status"
                className="text-[10px] font-bold uppercase tracking-[0.12em] text-primary"
              >
                Where this has got to
              </Label>
              <div className="mt-2 flex items-center gap-3">
                <Select
                  value={enquiry.status}
                  onValueChange={(value) => onStatusChange(enquiry.reference, value)}
                  disabled={saving}
                >
                  <SelectTrigger
                    id="finance-status"
                    className="h-10 w-[220px]"
                    data-testid="admin-finance-status-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FINANCE_STATUS_OPTIONS.map((option) => (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                        data-testid={`admin-finance-status-option-${option.value}`}
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />
                ) : null}
              </div>
              {enquiry.status_by ? (
                <p className="mt-2 text-xs text-muted-foreground">
                  Last changed by {enquiry.status_by}
                  {enquiry.status_at ? ` on ${formatDateTime(enquiry.status_at)}` : ""}
                </p>
              ) : null}
            </div>

            <div className="rounded-lg border border-border p-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-primary">
                Who is looking after this
              </p>
              <div className="mt-2">
                <OwnerPicker
                  value={enquiry.owner || ""}
                  members={team}
                  saving={savingOwner === enquiry.reference}
                  onAssign={(owner) => onAssignOwner(enquiry.reference, owner)}
                  onAddMember={onAddTeamMember}
                  testId="admin-finance-owner"
                  label="Who is looking after this"
                />
              </div>
              <p
                className="mt-2 text-xs text-muted-foreground"
                data-testid="admin-finance-owner-note"
              >
                {enquiry.owner
                  ? `${enquiry.owner} owns this enquiry${
                      enquiry.owner_by ? `, assigned by ${enquiry.owner_by}` : ""
                    }${enquiry.owner_at ? ` on ${formatDateTime(enquiry.owner_at)}` : ""}.`
                  : "Nobody owns this enquiry yet."}
              </p>
            </div>
          </div>
        </>
      ) : null}
    </DialogContent>
  </Dialog>
);
